import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { overtimeApprovalService } from '@/api/domains/overtime/overtimeApproval.service';
import { MAX_JUSTIFY_DETAIL_ROWS } from '@/api/domains/overtime/overtimeApproval.types';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { resolveDetailRowHours } from '@/hooks/overtime/overtimeApprovalDetailHours';
import { useUI } from '@/hooks/ui/useUI';
import { useTranslation } from '@/i18n';
import { ROUTE_DEFINITIONS } from '@/routes';
import { formatToDdMmYyyy } from '@/utils';
import type {
  LC_ApproveOvertimeDetailItemIn,
  LC_JustifyOvertimeDetailItemIn,
  LC_ReverseOvertimeDetailItemIn,
  OvertimeApprovalDetailRow,
} from '@/api/domains/overtime/overtimeApproval.types';
import type { OvertimeHourFormat } from '@/hooks/overtime/overtimeApprovalCounters';
import type { BannerPart } from '@/components/ui/banners';
import type { BulkActionItem } from '@/components/ui/tables/Table.types';


export interface UseOvertimeApprovalDetailActionsParams {
  rows: OvertimeApprovalDetailRow[];
  monthlyCutId: number | null;
  isMonthlyCutClosed: boolean;
  /** Misma función de identidad que recibe la tabla, para que selección y filas no se
   *  desalineen. */
  getRowId: (row: OvertimeApprovalDetailRow) => string;
  /** rowId → texto tecleado por el usuario en "Horas a aprobar". */
  editedHours: Map<string, string>;
  hourFormat: OvertimeHourFormat;
  /** Perfil efectivo `REVISOR`: horas de solo lectura y una única acción, Justificar. */
  isReviewer: boolean;
  /** `true` si el tipo elegido exige comentario libre (código `OTRO`, resuelto server-side). */
  requiresFreeCommentFor: (justificationTypeId: number | null) => boolean;
  /** Catálogo de tipos cargado y vacío: Justificar queda deshabilitada. */
  hasNoJustificationTypes: boolean;
  /** Identidad que cambia cuando el universo cambia (contador o búsqueda): limpia la selección.
   *  La paginación NO cambia: ahí el universo es el mismo y la selección entre páginas es
   *  deliberada. */
  selectionResetKey: unknown;
  /** Recarga la página vigente del detalle (tras justificar, que no navega de vuelta). */
  refetch: () => void;
}

export interface UseOvertimeApprovalDetailActionsResult {
  bulkActions: BulkActionItem[];
  selectedRowIds: Set<string | number>;
  onSelectionChange: (selected: OvertimeApprovalDetailRow[]) => void;
  /** Habilitación de la edición de horas: permiso de escritura, corte abierto y NO ser revisor. */
  canEditHours: boolean;
  /** Habilitación de cualquier acción que mute datos (Aprobar, Reversar, Justificar y el selector
   *  de tipo): permiso de escritura y corte abierto. El revisor NO edita horas pero sí justifica,
   *  así que su bloque in-page se gobierna con esto y no con `canEditHours`. */
  canSubmit: boolean;
  closedCutMessage: BannerPart[] | null;
  /** Envío en curso: bloquea la tabla hasta que responde el backend. */
  isSubmitting: boolean;
  /** Comentario obligatorio del aprobador, tecleado en la card sobre la tabla. */
  comment: string;
  onCommentChange: (value: string) => void;
  /** Se enciende al intentar aprobar sin comentario y pinta el aviso rojo bajo el input. */
  isCommentMissing: boolean;
  onApprove: () => Promise<void>;
  onReverse: () => Promise<void>;
  // ── Justificación del revisor (ASIS-5358) ────────────────────────────────
  justificationTypeId: number | null;
  onJustificationTypeChange: (justificationTypeId: number | null) => void;
  isJustificationTypeMissing: boolean;
  /** El tipo elegido exige comentario libre: la pantalla muestra el `CommentBox` debajo. */
  requiresFreeComment: boolean;
  freeComment: string;
  onFreeCommentChange: (value: string) => void;
  isFreeCommentMissing: boolean;
  onJustify: () => Promise<void>;
}

/**
 * Selección, acciones masivas, aprobación con comentario (ASIS-5356), reversa (ASIS-5357) y
 * justificación del revisor (ASIS-5358) del detalle de horas extras.
 *
 * El comentario obligatorio ya no se pide en un modal de confirmación: vive en una card sobre la
 * tabla y este hook es su dueño, de modo que Aprobar valida y envía en un solo paso, sin
 * confirmación intermedia. El selector de tipo de justificación del revisor sigue exactamente el
 * mismo patrón, también in-page.
 *
 * Desde que el detalle se pagina (ASIS-5358) la selección se guarda como FILAS y no solo como ids:
 * al aprobar o justificar hay que enviar filas que ya no están en `rows` porque el usuario cambió
 * de página. Por la misma razón la selección solo se limpia cuando cambia el universo
 * (`selectionResetKey`: contador o búsqueda), nunca al paginar.
 */
export function useOvertimeApprovalDetailActions(
  params: UseOvertimeApprovalDetailActionsParams,
): UseOvertimeApprovalDetailActionsResult {
  const {
    rows, monthlyCutId, isMonthlyCutClosed, getRowId, editedHours, hourFormat,
    isReviewer, requiresFreeCommentFor, hasNoJustificationTypes, selectionResetKey, refetch,
  } = params;

  const { t: translate } = useTranslation();
  const {
    showApiError, showSuccess, showWarning, showError,
  } = useUI();
  const { can } = usePagePermissions();
  const navigate = useNavigate();

  // El hook es la única fuente de verdad de la selección: la tabla recibe `selectedRowIds` en modo
  // controlado, así que limpiarla aquí (al cambiar de universo o tras operar) se refleja en los
  // checkboxes. Se guardan las filas completas porque la operación puede abarcar varias páginas.
  const [selectedRows, setSelectedRows] = useState<OvertimeApprovalDetailRow[]>([]);
  const [comment, setComment] = useState('');
  const [isCommentMissing, setIsCommentMissing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [justificationTypeId, setJustificationTypeId] = useState<number | null>(null);
  const [isJustificationTypeMissing, setIsJustificationTypeMissing] = useState(false);
  const [freeComment, setFreeComment] = useState('');
  const [isFreeCommentMissing, setIsFreeCommentMissing] = useState(false);
  // Ref (no estado) para que la guarda de reentrada lea el valor vigente de forma síncrona: un flag
  // de estado seguiría en false si la confirmación se dispara dos veces antes del re-render.
  const isSubmittingRef = useRef(false);

  // Cambiar de contador o de texto de búsqueda cambia el universo: mantener marcada una selección
  // que ya no corresponde a lo consultado sería operar a ciegas. Paginar y ordenar NO limpian.
  useEffect(() => {
    setSelectedRows([]);
  }, [selectionResetKey]);

  // Un corte cerrado no admite modificaciones. Se entrega en segmentos porque el Banner resalta el
  // prefijo "Atención:", igual que el resto de avisos de bloqueo del producto — y son exactamente
  // las mismas claves que usa el listado general.
  const closedCutMessage = useMemo<BannerPart[] | null>(
    () => (isMonthlyCutClosed
      ? [
        { text: translate('attendance:overtimeApproval.messages.closedCutEmphasis'), emphasis: true },
        { text: translate('attendance:overtimeApproval.messages.closedCut'), emphasis: false },
      ]
      : null),
    [isMonthlyCutClosed, translate],
  );

  // Habilitación de cualquier acción que mute datos, común a los dos perfiles.
  const canSubmit = can('edit') && !isMonthlyCutClosed;
  // El revisor NO edita horas: el criterio de aceptación y la pantalla legada coinciden en que su
  // columna de horas va deshabilitada.
  const canEditHours = canSubmit && !isReviewer;

  const selectedRowIds = useMemo<Set<string | number>>(
    () => new Set(selectedRows.map(getRowId)),
    [selectedRows, getRowId],
  );

  // La tabla emite únicamente las filas seleccionadas de la página visible: se reemplaza ese
  // subconjunto y se conserva lo seleccionado en otras páginas, para que marcar y desmarcar
  // funcionen entre páginas — mismo patrón que el listado general.
  const onSelectionChange = useCallback((selected: OvertimeApprovalDetailRow[]) => {
    const currentPageIds = new Set(rows.map(getRowId));
    const fromOtherPages = selectedRows.filter((row) => !currentPageIds.has(getRowId(row)));
    const merged = [...fromOtherPages, ...selected];

    if (merged.length > MAX_JUSTIFY_DETAIL_ROWS) {
      showWarning(translate('attendance:overtimeApproval.detail.justify.limitReached', {
        max: MAX_JUSTIFY_DETAIL_ROWS,
      }));
      setSelectedRows(merged.slice(0, MAX_JUSTIFY_DETAIL_ROWS));
      return;
    }

    setSelectedRows(merged);
  }, [rows, selectedRows, getRowId, showWarning, translate]);

  // Estado de las horas de cada fila seleccionada, resuelto una sola vez: alimenta tanto la guarda
  // de "supera lo calculado" como el payload de envío. Incluye filas de otras páginas, así que lo
  // tecleado antes de paginar viaja igual.
  const selectedRowsHours = useMemo(
    () => selectedRows.map((row) => ({
      row,
      hours: resolveDetailRowHours(row, editedHours.get(getRowId(row)), hourFormat),
    })),
    [selectedRows, editedHours, getRowId, hourFormat],
  );

  // Primera fila con horas fuera de rango. Se guarda la fila y no solo un booleano porque el aviso
  // debe NOMBRAR la jornada: con paginación la fila con error puede estar en otra página y un
  // mensaje genérico dejaría al usuario sin saber dónde corregir.
  const invalidHoursRow = useMemo(
    () => selectedRowsHours.find(({ hours }) => hours.isInvalid)?.row ?? null,
    [selectedRowsHours],
  );

  // Tecleando se apaga el aviso: el usuario ya está corrigiendo lo que se le señaló.
  const onCommentChange = useCallback((value: string) => {
    setComment(value);
    if (value.trim().length > 0) setIsCommentMissing(false);
  }, []);

  const onJustificationTypeChange = useCallback((nextTypeId: number | null) => {
    setJustificationTypeId(nextTypeId);
    if (nextTypeId !== null) setIsJustificationTypeMissing(false);
    // Cambiar de tipo invalida el comentario libre del tipo anterior.
    setIsFreeCommentMissing(false);
  }, []);

  const onFreeCommentChange = useCallback((value: string) => {
    setFreeComment(value);
    if (value.trim().length > 0) setIsFreeCommentMissing(false);
  }, []);

  const requiresFreeComment = requiresFreeCommentFor(justificationTypeId);

  /**
   * Aprueba directamente, sin confirmación intermedia. El comentario ya no vive en un modal sino en
   * la card sobre la tabla, así que su validación ocurre aquí: si está vacío se enciende el aviso
   * rojo bajo el input y no se llama al backend.
   */
  const onApprove = useCallback(async (): Promise<void> => {
    // Defensa en profundidad (Regla 12): bulkActions ya deriva de can('edit') y del corte abierto.
    if (!canEditHours || selectedRowsHours.length === 0) return;
    if (isSubmittingRef.current || monthlyCutId === null) return;

    // Las dos validaciones se evalúan juntas y recién después se corta: con un `return` temprano en
    // la del comentario, aprobar con horas fuera de rango y sin comentario ocultaba el aviso de las
    // horas hasta un segundo clic. Un clic debe mostrar todo lo que falta.
    const isCommentEmpty = comment.trim().length === 0;
    if (isCommentEmpty) setIsCommentMissing(true);

    // El backend también lo rechaza, pero avisar aquí evita un viaje inútil y deja el foco en la
    // celda a corregir. Va como `error` (rojo) y no como `warning`: es un dato inválido que impide
    // aprobar, igual que el valor de la celda que ya se pinta en rojo. Nombra la jornada porque la
    // fila puede haber quedado en otra página.
    if (invalidHoursRow !== null) {
      showError(translate('attendance:overtimeApproval.detail.approve.hoursExceedCalculated', {
        employeeName: invalidHoursRow.employeeName,
        workDate: formatToDdMmYyyy(invalidHoursRow.workDate),
      }));
    }

    if (isCommentEmpty || invalidHoursRow !== null) return;

    const items: LC_ApproveOvertimeDetailItemIn[] = selectedRowsHours.map(({ row, hours }) => ({
      employeeId: row.employeeId,
      workDate: row.workDate,
      overtimeTypeCode: row.overtimeTypeCode,
      scheduleId: row.scheduleId,
      isWeeklyCompensation: row.isWeeklyCompensation,
      // `invalidHoursRow` ya descartó los null; el 0 explícito es válido y des-aprueba el tramo.
      hoursToApprove: hours.decimalValue ?? 0,
      knownApprovedHours: row.approvedHours.decimalValue,
    }));

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      const response = await overtimeApprovalService.approveDetail({
        monthlyCutId,
        comment: comment.trim(),
        items,
      });

      if (!response.success || !response.data) {
        // Se conservan comentario y selección: el usuario debe poder reintentar sin reescribir.
        showApiError(response, translate('attendance:overtimeApproval.detail.approve.error'));
        return;
      }

      const { totalApproved, totalAlreadyResolved, totalNotProcessed } = response.data;
      const hasDiscarded = totalAlreadyResolved > 0 || totalNotProcessed > 0;

      // Mismos textos que la aprobación masiva del listado: los criterios de aceptación piden
      // literalmente esos mensajes, así que se reutilizan las claves en vez de duplicarlas.
      if (!hasDiscarded) {
        showSuccess(translate('attendance:overtimeApproval.bulkApprove.success'));
      } else if (totalApproved > 0) {
        showSuccess(translate('attendance:overtimeApproval.bulkApprove.summarySuccess', {
          count: totalApproved,
        }));
      }

      if (totalAlreadyResolved > 0) {
        showWarning(translate('attendance:overtimeApproval.bulkSelection.summarySkipped', {
          count: totalAlreadyResolved,
        }));
      }

      if (totalNotProcessed > 0) {
        showWarning(translate('attendance:overtimeApproval.bulkApprove.summaryNotProcessed', {
          count: totalNotProcessed,
        }));
      }

      setSelectedRows([]);
      setComment('');
      setIsCommentMissing(false);
      void navigate(ROUTE_DEFINITIONS.APROBAR_HHEE.path);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [
    canEditHours, monthlyCutId, comment, selectedRowsHours, invalidHoursRow,
    showApiError, showSuccess, showWarning, showError, translate, navigate,
  ]);

  /**
   * Reversa las filas seleccionadas (ASIS-5357). No pide comentario — la card sobre la tabla es
   * exclusiva de Aprobar — ni muestra confirmación intermedia: un clic ejecuta y vuelve al listado,
   * igual que Aprobar.
   *
   * Envía las horas aprobadas VIGENTES de cada fila (`approvedHours`), nunca lo tecleado en la
   * columna "Horas a aprobar": esa edición pertenece a la aprobación, y usarla aquí haría que el
   * backend viera un desfase y descartara la fila como resuelta por otro usuario.
   */
  const onReverse = useCallback(async (): Promise<void> => {
    // Defensa en profundidad (Regla 12): bulkActions ya deriva de can('edit') y del corte abierto.
    if (!canEditHours || selectedRows.length === 0) return;
    if (isSubmittingRef.current || monthlyCutId === null) return;

    const items: LC_ReverseOvertimeDetailItemIn[] = selectedRows.map((row) => ({
      employeeId: row.employeeId,
      workDate: row.workDate,
      overtimeTypeCode: row.overtimeTypeCode,
      scheduleId: row.scheduleId,
      isWeeklyCompensation: row.isWeeklyCompensation,
      knownApprovedHours: row.approvedHours.decimalValue,
    }));

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      const response = await overtimeApprovalService.reverseDetail({ monthlyCutId, items });

      if (!response.success || !response.data) {
        // Se conserva la selección: el usuario debe poder reintentar sin volver a marcar las filas.
        showApiError(response, translate('attendance:overtimeApproval.bulkReverse.error'));
        return;
      }

      const { totalReversed, totalAlreadyResolved, totalNotProcessed } = response.data;
      const hasDiscarded = totalAlreadyResolved > 0 || totalNotProcessed > 0;

      // Mismos textos que la reversa masiva del listado: los criterios de aceptación piden
      // literalmente esos mensajes, así que se reutilizan las claves en vez de duplicarlas. Las
      // filas descartadas en silencio por el backend no suman a ningún total y por eso no avisan.
      //
      // Ambos mensajes de éxito exigen `totalReversed > 0`: `hasDiscarded` solo mira los totales,
      // y las filas descartadas en silencio (outcome 'ignored': ya estaban en Pendiente) quedan
      // fuera de los tres. Reversando una selección enteramente Pendiente el backend no escribe
      // nada (es un no-op: sin transacción ni auditoría) y llegan los tres totales en 0, así que
      // sin esta guarda se anunciaba éxito por una operación vacía. Ahí no se avisa nada: advertir
      // de que no se reversó lo que ya estaba sin aprobar sería el ruido que el backend evita.
      if (totalReversed > 0 && !hasDiscarded) {
        showSuccess(translate('attendance:overtimeApproval.bulkReverse.success'));
      } else if (totalReversed > 0) {
        showSuccess(translate('attendance:overtimeApproval.bulkReverse.summarySuccess', {
          count: totalReversed,
        }));
      }

      if (totalAlreadyResolved > 0) {
        showWarning(translate('attendance:overtimeApproval.bulkSelection.summarySkipped', {
          count: totalAlreadyResolved,
        }));
      }

      if (totalNotProcessed > 0) {
        showWarning(translate('attendance:overtimeApproval.bulkReverse.summaryNotProcessed', {
          count: totalNotProcessed,
        }));
      }

      setSelectedRows([]);
      void navigate(ROUTE_DEFINITIONS.APROBAR_HHEE.path);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [
    canEditHours, monthlyCutId, selectedRows,
    showApiError, showSuccess, showWarning, translate, navigate,
  ]);

  /**
   * Justifica las filas seleccionadas (ASIS-5358). No viajan horas: el backend relee el detalle y
   * las resuelve. A diferencia de Aprobar y Reversar NO navega de vuelta al listado — el revisor
   * debe ver el efecto de su acción (estado Justificada y contadores al día), que es justamente lo
   * que la pantalla legada no hacía.
   */
  const onJustify = useCallback(async (): Promise<void> => {
    // Defensa en profundidad (Regla 12): bulkActions ya deriva de can('edit') y del corte abierto.
    if (!canSubmit || selectedRows.length === 0) return;
    if (isSubmittingRef.current || monthlyCutId === null) return;

    // Ambas validaciones se evalúan juntas: un solo clic debe mostrar todo lo que falta.
    const isTypeMissing = justificationTypeId === null;
    if (isTypeMissing) setIsJustificationTypeMissing(true);

    const isFreeCommentEmpty = requiresFreeComment && freeComment.trim().length === 0;
    if (isFreeCommentEmpty) setIsFreeCommentMissing(true);

    if (isTypeMissing || isFreeCommentEmpty) return;

    const items: LC_JustifyOvertimeDetailItemIn[] = selectedRows.map((row) => ({
      employeeId: row.employeeId,
      workDate: row.workDate,
      scheduleId: row.scheduleId,
      overtimeTypeCode: row.overtimeTypeCode,
    }));

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      const response = await overtimeApprovalService.justifyDetail({
        monthlyCutId,
        justificationTypeId,
        // El comentario libre está PROHIBIDO cuando el tipo no lo exige: se omite, no se manda ''.
        ...(requiresFreeComment ? { freeComment: freeComment.trim() } : {}),
        items,
      });

      if (!response.success || !response.data) {
        // Se conservan tipo, comentario y selección: el usuario debe poder reintentar sin rearmar.
        showApiError(response, translate('attendance:overtimeApproval.detail.justify.error'));
        return;
      }

      const { totalJustified, totalNotProcessed } = response.data;

      // Texto literal del criterio de aceptación: dice "aprobadas" aunque la acción sea justificar.
      if (totalNotProcessed === 0) {
        showSuccess(translate('attendance:overtimeApproval.detail.justify.success'));
      } else if (totalJustified > 0) {
        showSuccess(translate('attendance:overtimeApproval.detail.justify.summarySuccess', {
          count: totalJustified,
        }));
      }

      if (totalNotProcessed > 0) {
        showWarning(translate('attendance:overtimeApproval.detail.justify.summaryNotProcessed', {
          count: totalNotProcessed,
        }));
      }

      setSelectedRows([]);
      refetch();
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [
    canSubmit, monthlyCutId, selectedRows, justificationTypeId, requiresFreeComment, freeComment,
    showApiError, showSuccess, showWarning, translate, refetch,
  ]);

  const bulkActions = useMemo<BulkActionItem[]>(() => {
    // El revisor no aprueba ni reversa: en el detalle manda su propia acción. La autorización real
    // la aplica el backend con 403 en los cuatro endpoints de aprobar/reversar.
    if (isReviewer) {
      return [{
        label: translate('attendance:overtimeApproval.detail.justify.action'),
        icon: 'checkCircle',
        // Un tipo sin elegir NO deshabilita: el usuario debe poder presionar para que el aviso rojo
        // le explique qué falta. Un catálogo vacío SÍ: no hay nada que elegir y el envío sin tipo
        // es justamente lo que el SP rechaza.
        onClick: () => { void onJustify(); },
        disabled: !canSubmit
          || selectedRowIds.size === 0
          || isSubmitting
          || hasNoJustificationTypes,
      }];
    }

    return [
      {
        label: translate('attendance:overtimeApproval.detail.approve.action'),
        icon: 'checkCircle',
        // Un comentario vacío NO deshabilita: el usuario debe poder presionar para que el aviso
        // rojo le explique qué falta, en vez de quedarse con un botón muerto sin motivo visible.
        onClick: () => { void onApprove(); },
        disabled: !canEditHours || selectedRowIds.size === 0 || isSubmitting,
      },
      // Reversar (ASIS-5357). A diferencia de Aprobar, sí se deshabilita sin selección: no hay
      // ningún aviso que mostrar al presionarla vacía. Descargar vive sobre la tabla, siempre
      // visible.
      {
        label: translate('attendance:overtimeApproval.bulkReverse.action'),
        icon: 'sync',
        variant: 'danger',
        onClick: () => { void onReverse(); },
        disabled: !canEditHours || selectedRowIds.size === 0 || isSubmitting,
      },
    ];
  }, [
    isReviewer, translate, onApprove, onReverse, onJustify, canEditHours, canSubmit,
    selectedRowIds.size, isSubmitting, hasNoJustificationTypes,
  ]);

  return {
    bulkActions,
    selectedRowIds,
    onSelectionChange,
    canEditHours,
    canSubmit,
    closedCutMessage,
    isSubmitting,
    comment,
    onCommentChange,
    isCommentMissing,
    onApprove,
    onReverse,
    justificationTypeId,
    onJustificationTypeChange,
    isJustificationTypeMissing,
    requiresFreeComment,
    freeComment,
    onFreeCommentChange,
    isFreeCommentMissing,
    onJustify,
  };
}

export default useOvertimeApprovalDetailActions;
